/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { v4 as uuid_v4 } from "uuid";

import { createEventEmitter, emitEvent } from "@adapptio/lib-shared";
import { Dialog } from "../../../dialogs/Dialog";
import { IEditor } from "../../../IEditor";
import { ResourceService } from "../../../resources/ResourceService";
import { ImportBlueprintDialogComponent } from "./ImportBlueprintDialogComponent";
import { ToastService } from "../../../toasts/ToastService";
import { TOAST_TYPE } from "../../../toasts/IToast";
import { normalizeResourcePath } from "../../../resources/ResourceHelpers";
import { IEditorApiService } from "../../../api/IEditorApiService";
import { ErrorTracker } from "@adapptio/lib-components";

export interface ImportBlueprintDialog_Params {
	resourceId: string;
}

export interface ImportBlueprintDialog_Result {
	resourceId: string;
}

export interface IImportBlueprintDialog_CreateParams {
	basePath: string;
	name: string;
	file: File;
}

export class ImportBlueprintDialog extends Dialog<
	ImportBlueprintDialog_Params,
	ImportBlueprintDialog_Result
> {
	/** Services */
	private resourceSvc: ResourceService;
	private toastSvc: ToastService;

	/** API Service Instance */
	private apiService: IEditorApiService;

	/** New resource ID */
	private resourceId: string;

	/** Parrent resource ID */
	private parrentResourceId: string;

	/** Document model */
	// private viewModel: ViewModel;

	/** If the view is being saved */
	private saveInProgress = false;

	/** Event emitted when save progress changes */
	public onSaveStateChange = createEventEmitter<void>();

	/**
	 * Dialog constructor
	 *
	 * @param dc Dependency container
	 * @param id Dialog ID
	 * @param params Open params
	 */
	public constructor(dc: IEditor, id: string, params: ImportBlueprintDialog_Params) {
		super(dc, id, params);

		this.resourceSvc = this.dc.get("resourceService");
		this.toastSvc = dc.get("toastService");
		this.apiService = dc.get("apiService");

		this.parrentResourceId = params.resourceId;

		this.resourceId = uuid_v4();
	}

	public getComponent(): typeof ImportBlueprintDialogComponent {
		return ImportBlueprintDialogComponent;
	}

	/**
	 * Get a resource node for select folders when import resource
	 */
	public getParrentResourceNode() {
		if (this.parrentResourceId) {
			const resource = this.resourceSvc.getResourceById(this.parrentResourceId);

			const resourceNode = {
				id: resource.descriptor.id,
				name: resource.descriptor.name,
				resourceType: resource.descriptor.resourceType,
				basePath: resource.descriptor.basePath,
				isOpened: true,
				canWrite: resource.descriptor.attributes.allowWrite,
				canRename: resource.descriptor.attributes.allowRename,
				canDelete: resource.descriptor.attributes.allowRemove,
				isValid: resource.descriptor.parseData.valid,
				children: []
			};
			return resourceNode;
		}

		return null;
	}

	/**
	 * Creates a resource
	 * Returns false if create is already in progress or resource was successfully created
	 */
	public async import(params: IImportBlueprintDialog_CreateParams): Promise<boolean> {
		if (this.saveInProgress) {
			return false;
		}

		const file = params.file;

		if (!file) {
			return false;
		}

		this.saveInProgress = true;
		emitEvent(this.onSaveStateChange);

		try {
			const reader = new FileReader();
			reader.onload = async (e) => {
				const content = `${e.target.result}`;
				const basePath = params.basePath;
				const name = params.name;

				const newResource = await this.resourceSvc.importBlueprint(
					content,
					name,
					basePath,
					this.resourceId
				);

				if (!newResource) {
					throw new Error(`Newly created resource with ID '${this.resourceId}' does not exists.`);
				}
			};
			reader.readAsText(file);

			this.toastSvc.add({
				type: TOAST_TYPE.SUCCESS,
				// @todo Translate
				message: "Resource has been imported.",
				timeoutMs: 2500
			});

			this.close({
				resourceId: this.resourceId
			});
		} catch (err) {
			const errorTracker = ErrorTracker.getInstance();
			errorTracker.captureException(err);

			this.toastSvc.add({
				type: TOAST_TYPE.ERROR,
				// @todo Translate
				message: "Failed to importend resource. Please try it again in a while.",
				details: String(err)
			});
		} finally {
			this.saveInProgress = false;
			emitEvent(this.onSaveStateChange);
		}

		return true;
	}

	/**
	 * Returns if a view is being saved
	 */
	public isBeingSaved(): boolean {
		return this.saveInProgress;
	}

	/**
	 * Returns if a resource with given baseName and name already exists
	 *
	 * @param basePath Base path
	 * @param name Name
	 */
	public checkIfBlueprintExists(basePath: string, name: string): boolean {
		return !!this.resourceSvc.getResourceByPath(
			normalizeResourcePath(basePath, name, { sanitize: true })
		);
	}
}
